import React from 'react';
import { normalizeRecurringSettings } from 'src/modules/campaigns/helpers';
import { RecurringPeriod, RecurringSettings } from 'src/modules/campaigns/types';
import { RecurringSettingsForm } from './RecurringSettingsForm';
import { useCampaignFormContext, useIsReadOnly } from '../../context';
import {
  getRecurringIntervalMin,
  validateRecurringSettings,
  validateRecurringSettingsOrder,
} from '../../helpers/recurring';

/**
 * Recurring schedule settings. Reuses the shared RecurringSettingsForm — the
 * hand-rolled version this replaced diverged on every control (chips vs toggles,
 * date pickers vs native date inputs) and exposed startTime/endTime editors that
 * are deliberately kept out of the form.
 *
 * Error display: before the first submit only the ordering errors show;
 * afterwards the full required-field set does.
 */
export const RecurringSettingsField: React.FC = () => {
  const { values, setField, isSubmitted } = useCampaignFormContext();
  const disabled = useIsReadOnly();

  const settings = normalizeRecurringSettings(values.recurringSettings);
  const errors = isSubmitted
    ? validateRecurringSettings(settings)
    : validateRecurringSettingsOrder(settings);

  const commit = (next: RecurringSettings) =>
    setField('recurringSettings', next);

  const handleSettingsChange = <K extends keyof RecurringSettings>(
    name: K,
    value: RecurringSettings[K],
  ) => commit({ ...settings, [name]: value });

  // Switching period raises the interval to that period's minimum.
  const handlePeriodChange = (period: RecurringPeriod) =>
    commit({
      ...settings,
      period,
      interval: Math.max(settings.interval, getRecurringIntervalMin(period)),
    });

  const handleDayToggle = (day: number) =>
    commit({
      ...settings,
      daysOfWeek: settings.daysOfWeek.includes(day)
        ? settings.daysOfWeek.filter(currentDay => currentDay !== day)
        : [...settings.daysOfWeek, day],
    });

  return (
    <RecurringSettingsForm
      disabled={disabled}
      errors={errors}
      recurringSettings={settings}
      onDayToggle={handleDayToggle}
      onPeriodChange={handlePeriodChange}
      onSettingsChange={handleSettingsChange}
    />
  );
};
